import {useCallback, useMemo} from 'react';
import {addDoc, collection, deleteDoc, deleteField, doc, query, serverTimestamp, updateDoc,} from 'firebase/firestore';
import {useCollection} from 'react-firebase-hooks/firestore';
import {Subject, SubjectDocument, SubjectInsertOptions, SubjectUpdateOptions} from '../types/Subject';
import {firestore} from "../App";

// --- Hook ---
interface UseSubjectManagerProps {
    plannerId: string;
}

export function useSubjectManager({plannerId}: UseSubjectManagerProps) {

    // 1. READ Subjects
    const subjectsQuery = useMemo(() => {
        if (!plannerId) return null;

        return query(
            collection(firestore, 'syncedPlanners', plannerId, 'subjects')
        );
    }, [plannerId]);

    const [subjectsSnapshot, loadingSubjects, errorSubjects] = useCollection(subjectsQuery);

    const subjects = useMemo(() => {
        return subjectsSnapshot?.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as Subject)) || [];
    }, [subjectsSnapshot]);

    /**
     * Inserts a subject document in the database.
     */
    const createSubject = useCallback(async (options: SubjectInsertOptions) => {
        if (!plannerId) return;

        const subjectDoc: Omit<SubjectDocument, 'created_on'> & { created_on: any } = {
            plannerId: plannerId,
            name: options.name,
            color: options.color,
            note: options.note,
            room: options.room,
            created_on: serverTimestamp(),
        };

        // Clean up undefined fields
        if (subjectDoc.note === undefined) delete subjectDoc.note;
        if (subjectDoc.room === undefined) delete subjectDoc.room;

        const colRef = collection(firestore, 'syncedPlanners', plannerId, 'subjects');
        await addDoc(colRef, subjectDoc);

    }, [plannerId]);

    /**
     * Updates a subject document in the database.
     * NOTE: The signature is changed from (object, options) to (subjectId, options)
     * to match the other Firestore hooks.
     */
    const updateSubject = useCallback(async (subjectId: string, options: SubjectUpdateOptions) => {
        if (!plannerId) return;

        const docRef = doc(firestore, 'syncedPlanners', plannerId, 'subjects', subjectId);

        const updateData = {
            name: options.name,
            color: options.color,
            note: options.note ?? deleteField(),
            room: options.room ?? deleteField(),
        };

        await updateDoc(docRef, updateData);
    }, [plannerId]);

    /**
     * Deletes a subject from the database.
     */
    const deleteSubject = useCallback(async (subjectId: string) => {
        if (!plannerId) return;

        const docRef = doc(firestore, 'syncedPlanners', plannerId, 'subjects', subjectId);
        await deleteDoc(docRef);
    }, [plannerId]);

    return {
        subjects,
        loadingSubjects,
        errorSubjects,
        createSubject,
        updateSubject,
        deleteSubject
    };
}